import { useEffect, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import type {
  TransportOperationName,
  TransportOperationResponse,
  TransportOperationVersionNumber,
} from "@/types_api/backend/transport/operations"

import type { TransportRequestError } from "../utils/transportClient"
import { reportTransportError } from "../utils/transportErrorReporting"
import {
  executeTransportOperation,
  resolveTransportOperationName,
} from "./transportOperationRuntime"
import { getTransportQueryKey } from "./transportQueryKeys"
import type {
  TransportClientOptions,
  TransportHookOptions,
  TransportHookParams,
  TransportQueryKey,
  TransportRuntimeHookParams,
  TransportVersionedHookParams,
  TransportVersionedOperationGroup,
  TransportVersionedOperationName,
} from "./transportQueryTypes"
import { useTransportOperationContext } from "./useTransportOperationContext"

export function useTransportQuery<
  Name extends TransportOperationName,
  TData = TransportOperationResponse<Name>,
>(
  params: TransportHookParams<Name> & {
    clientOptions?: TransportClientOptions
    options?: TransportHookOptions<Name, TData>
  },
): UseQueryResult<TData, TransportRequestError>

export function useTransportQuery<
  Group extends TransportVersionedOperationGroup,
  Version extends TransportOperationVersionNumber<Group>,
  Name extends TransportVersionedOperationName<Group, Version>,
  TData = TransportOperationResponse<Name>,
>(
  params: TransportVersionedHookParams<Group, Version> & {
    clientOptions?: TransportClientOptions
    options?: TransportHookOptions<Name, TData>
  },
): UseQueryResult<TData, TransportRequestError>

export function useTransportQuery<
  Name extends TransportOperationName,
  TData = TransportOperationResponse<Name>,
>(params: unknown): UseQueryResult<TData, TransportRequestError> {
  const runtimeParams = params as TransportRuntimeHookParams<
    Name,
    TransportHookOptions<Name, TData>
  >
  const { clientOptions, options } = runtimeParams
  const operation = resolveTransportOperationName(runtimeParams)
  const {
    reportErrors = true,
    abortOnUnmount = false,
    ...queryOptions
  } = options ?? {}
  const request = "request" in runtimeParams ? runtimeParams.request : undefined
  const requestReady = !("request" in runtimeParams) || request != null
  const typedOperation = operation as Name
  const typedRequest = request
  const {
    getClient,
    isReady,
    organizationSlug,
    queryOrganizationSlug,
    queryWorkspaceId,
  } = useTransportOperationContext(operation, clientOptions)

  const queryKey = useMemo(
    () =>
      getTransportQueryKey(
        typedOperation,
        typedRequest,
        queryOrganizationSlug,
        queryWorkspaceId,
      ),
    [queryOrganizationSlug, queryWorkspaceId, typedOperation, typedRequest],
  )

  const enabled = (options?.enabled ?? true) && isReady && requestReady

  const query = useQuery<
    TransportOperationResponse<Name>,
    TransportRequestError,
    TData,
    TransportQueryKey<Name>
  >({
    ...CACHE_PROFILES.standard,
    ...queryOptions,
    enabled,
    queryKey,
    queryFn: (context) => {
      if (!requestReady) {
        return Promise.resolve(
          null as unknown as TransportOperationResponse<Name>,
        )
      }

      return executeTransportOperation(
        getClient(),
        typedOperation,
        typedRequest ?? undefined,
        abortOnUnmount ? { signal: context.signal } : undefined,
      )
    },
  })

  useEffect(() => {
    if (reportErrors && query.error) {
      reportTransportError({
        error: query.error,
        kind: "query",
        operation,
        organizationSlug,
        errorUpdatedAt: query.errorUpdatedAt,
      })
    }
  }, [
    operation,
    organizationSlug,
    query.error,
    query.errorUpdatedAt,
    reportErrors,
  ])

  return query
}
